const value = (values, key, fallback = 0) => {
  const parsed = Number(values && values[key])
  return Number.isFinite(parsed) ? parsed : fallback
}

const positive = number => Math.max(number, 0)

const round = (number, digits = 1) => {
  const factor = Math.pow(10, digits)
  return Math.round((number + Number.EPSILON) * factor) / factor
}

const formatNumber = (number, digits = 1) => round(number, digits).toLocaleString('en-US')

const currency = number => `$${Math.round(number).toLocaleString('en-US')}`

const whole = number => Math.ceil(positive(number)).toLocaleString('en-US')

const percentFactor = number => 1 + positive(number) / 100

const areaFrom = values => positive(value(values, 'length') * value(values, 'width'))

const result = (primaryLabel, primaryValue, summary, results, steps) => ({
  primaryLabel,
  primaryValue,
  summary,
  results,
  steps
})

const engines = {
  paint(values) {
    const gross = value(values, 'wallLength') * value(values, 'wallHeight')
    const net = positive(gross - value(values, 'openings'))
    const coated = net * value(values, 'coats')
    const adjusted = coated * percentFactor(value(values, 'waste'))
    const gallons = adjusted / Math.max(value(values, 'coverage'), 1)
    const buyGallons = Math.ceil(gallons)

    return result(
      'Paint to buy',
      `${whole(buyGallons)} gallons`,
      `Plan for ${formatNumber(adjusted)} sq ft of coated coverage after openings, coats, and waste.`,
      [
        { label: 'Gross wall area', value: `${formatNumber(gross)} sq ft` },
        { label: 'Net wall area', value: `${formatNumber(net)} sq ft` },
        { label: 'Adjusted coated area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Raw gallons', value: `${formatNumber(gallons, 2)} gallons` }
      ],
      [
        `Gross wall area = ${value(values, 'wallLength')} ft x ${value(values, 'wallHeight')} ft = ${formatNumber(gross)} sq ft.`,
        `Net wall area = ${formatNumber(gross)} - ${value(values, 'openings')} = ${formatNumber(net)} sq ft.`,
        `Coated area = ${formatNumber(net)} x ${value(values, 'coats')} coats x ${percentFactor(value(values, 'waste')).toFixed(2)} waste factor = ${formatNumber(adjusted)} sq ft.`,
        `Gallons = ${formatNumber(adjusted)} / ${value(values, 'coverage')} = ${formatNumber(gallons, 2)}, rounded up to ${whole(buyGallons)} gallons.`
      ]
    )
  },
  paintCost(values) {
    const coated = value(values, 'paintableArea') * value(values, 'coats') * percentFactor(value(values, 'waste'))
    const gallons = coated / Math.max(value(values, 'coverage'), 1)
    const buyGallons = Math.ceil(gallons)
    const paintTotal = buyGallons * value(values, 'pricePerGallon')
    const total = paintTotal + value(values, 'supplies')

    return result(
      'Estimated painting cost',
      currency(total),
      `${whole(buyGallons)} gallons plus ${currency(value(values, 'supplies'))} in prep and supplies.`,
      [
        { label: 'Adjusted coated area', value: `${formatNumber(coated)} sq ft` },
        { label: 'Gallons to buy', value: `${whole(buyGallons)} gallons` },
        { label: 'Paint cost', value: currency(paintTotal) },
        { label: 'Total material budget', value: currency(total) }
      ],
      [
        `Adjusted coated area = ${value(values, 'paintableArea')} sq ft x ${value(values, 'coats')} coats x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(coated)} sq ft.`,
        `Gallons = ${formatNumber(coated)} / ${value(values, 'coverage')} = ${formatNumber(gallons, 2)}, rounded up to ${whole(buyGallons)} gallons.`,
        `Paint cost = ${whole(buyGallons)} x ${currency(value(values, 'pricePerGallon'))} = ${currency(paintTotal)}.`,
        `Total = ${currency(paintTotal)} + supplies ${currency(value(values, 'supplies'))} = ${currency(total)}.`
      ]
    )
  },
  wallArea(values) {
    const gross = value(values, 'wallLength') * value(values, 'wallHeight')
    const openings = value(values, 'doorArea') + value(values, 'windowArea')
    const net = positive(gross - openings)

    return result(
      'Net wall area',
      `${formatNumber(net)} sq ft`,
      'Use this net surface area for paint, wallpaper, wall paneling, or other finish estimates.',
      [
        { label: 'Gross wall area', value: `${formatNumber(gross)} sq ft` },
        { label: 'Openings deducted', value: `${formatNumber(openings)} sq ft` },
        { label: 'Net wall area', value: `${formatNumber(net)} sq ft` }
      ],
      [
        `Gross wall area = ${value(values, 'wallLength')} ft x ${value(values, 'wallHeight')} ft = ${formatNumber(gross)} sq ft.`,
        `Openings = door area ${value(values, 'doorArea')} + window area ${value(values, 'windowArea')} = ${formatNumber(openings)} sq ft.`,
        `Net wall area = ${formatNumber(gross)} - ${formatNumber(openings)} = ${formatNumber(net)} sq ft.`
      ]
    )
  },
  ceilingPaint(values) {
    const area = areaFrom(values)
    const adjusted = area * value(values, 'coats') * percentFactor(value(values, 'waste'))
    const gallons = adjusted / Math.max(value(values, 'coverage'), 1)
    const buyGallons = Math.ceil(gallons)

    return result(
      'Ceiling paint to buy',
      `${whole(buyGallons)} gallons`,
      `The ceiling area is ${formatNumber(area)} sq ft before coats and waste.`,
      [
        { label: 'Ceiling area', value: `${formatNumber(area)} sq ft` },
        { label: 'Adjusted coated area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Raw gallons', value: `${formatNumber(gallons, 2)} gallons` }
      ],
      [
        `Ceiling area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted area = ${formatNumber(area)} x ${value(values, 'coats')} coats x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Gallons = ${formatNumber(adjusted)} / ${value(values, 'coverage')} = ${formatNumber(gallons, 2)}, rounded up to ${whole(buyGallons)}.`
      ]
    )
  },
  cabinetPaint(values) {
    const faceArea = value(values, 'doors') * 8 + value(values, 'drawers') * 3
    const surface = faceArea + value(values, 'sideArea')
    const adjusted = surface * value(values, 'coats') * percentFactor(value(values, 'waste'))
    const gallons = adjusted / Math.max(value(values, 'coverage'), 1)
    const buyGallons = Math.ceil(gallons)

    return result(
      'Cabinet paint to buy',
      `${whole(buyGallons)} gallons`,
      `Estimated cabinet surface is ${formatNumber(surface)} sq ft before coats and waste.`,
      [
        { label: 'Door and drawer area', value: `${formatNumber(faceArea)} sq ft` },
        { label: 'Total cabinet surface', value: `${formatNumber(surface)} sq ft` },
        { label: 'Adjusted coated area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Raw gallons', value: `${formatNumber(gallons, 2)} gallons` }
      ],
      [
        `Door and drawer area = doors x 8 sq ft + drawer fronts x 3 sq ft = ${formatNumber(faceArea)} sq ft.`,
        `Total surface = ${formatNumber(faceArea)} + exposed sides ${value(values, 'sideArea')} = ${formatNumber(surface)} sq ft.`,
        `Adjusted coated area = ${formatNumber(surface)} x ${value(values, 'coats')} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Gallons = ${formatNumber(adjusted)} / ${value(values, 'coverage')} = ${formatNumber(gallons, 2)}, rounded up to ${whole(buyGallons)}.`
      ]
    )
  },
  wallpaper(values) {
    const gross = value(values, 'wallLength') * value(values, 'wallHeight')
    const net = positive(gross - value(values, 'openings'))
    const adjusted = net * percentFactor(value(values, 'waste'))
    const rolls = Math.ceil(adjusted / Math.max(value(values, 'rollCoverage'), 1))

    return result(
      'Wallpaper rolls to buy',
      `${whole(rolls)} rolls`,
      `Plan for ${formatNumber(adjusted)} sq ft after subtracting openings and adding pattern waste.`,
      [
        { label: 'Net wall area', value: `${formatNumber(net)} sq ft` },
        { label: 'Adjusted wallpaper area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Roll coverage', value: `${formatNumber(value(values, 'rollCoverage'))} sq ft` }
      ],
      [
        `Gross wall area = ${value(values, 'wallLength')} ft x ${value(values, 'wallHeight')} ft = ${formatNumber(gross)} sq ft.`,
        `Net wall area = ${formatNumber(gross)} - openings ${value(values, 'openings')} = ${formatNumber(net)} sq ft.`,
        `Adjusted area = ${formatNumber(net)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Rolls = ${formatNumber(adjusted)} / ${value(values, 'rollCoverage')} = ${whole(rolls)} rolls.`
      ]
    )
  },
  flooring(values) {
    const area = areaFrom(values)
    const adjusted = area * percentFactor(value(values, 'waste'))
    const boxes = Math.ceil(adjusted / Math.max(value(values, 'boxCoverage'), 1))

    return result(
      'Flooring to buy',
      `${whole(boxes)} boxes`,
      `Order at least ${formatNumber(adjusted)} sq ft including waste.`,
      [
        { label: 'Room area', value: `${formatNumber(area)} sq ft` },
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Box coverage', value: `${formatNumber(value(values, 'boxCoverage'))} sq ft` }
      ],
      [
        `Room area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted area = ${formatNumber(area)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Boxes = ${formatNumber(adjusted)} / ${value(values, 'boxCoverage')} = ${whole(boxes)} boxes.`
      ]
    )
  },
  flooringCost(values) {
    const area = areaFrom(values)
    const adjusted = area * percentFactor(value(values, 'waste'))
    const boxes = Math.ceil(adjusted / Math.max(value(values, 'boxCoverage'), 1))
    const material = adjusted * value(values, 'pricePerSqFt')
    const labor = area * value(values, 'laborPerSqFt')
    const total = material + labor

    return result(
      'Estimated installed cost',
      currency(total),
      `Plan for ${formatNumber(adjusted)} sq ft of hardwood and ${whole(boxes)} cartons.`,
      [
        { label: 'Adjusted flooring area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Cartons to buy', value: `${whole(boxes)} cartons` },
        { label: 'Material cost', value: currency(material) },
        { label: 'Labor allowance', value: currency(labor) }
      ],
      [
        `Base area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted area = ${formatNumber(area)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Material cost = ${formatNumber(adjusted)} x ${currency(value(values, 'pricePerSqFt'))} = ${currency(material)}.`,
        `Installed estimate = material ${currency(material)} + labor ${currency(labor)} = ${currency(total)}.`
      ]
    )
  },
  carpet(values) {
    const area = areaFrom(values)
    const adjusted = area * percentFactor(value(values, 'waste'))
    const squareYards = adjusted / 9
    const rollLength = adjusted / Math.max(value(values, 'carpetWidth'), 1)
    const material = squareYards * value(values, 'pricePerSqYd')

    return result(
      'Carpet to buy',
      `${formatNumber(squareYards)} sq yd`,
      `Plan for about ${formatNumber(rollLength)} linear feet from a ${value(values, 'carpetWidth')} ft roll.`,
      [
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Square yards', value: `${formatNumber(squareYards)} sq yd` },
        { label: 'Estimated roll length', value: `${formatNumber(rollLength)} linear ft` },
        { label: 'Material cost', value: currency(material) }
      ],
      [
        `Area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted area = ${formatNumber(area)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Square yards = ${formatNumber(adjusted)} / 9 = ${formatNumber(squareYards)} sq yd.`,
        `Roll length = ${formatNumber(adjusted)} / ${value(values, 'carpetWidth')} ft = ${formatNumber(rollLength)} linear ft.`
      ]
    )
  },
  laminate(values) {
    const area = areaFrom(values)
    const adjusted = area * percentFactor(value(values, 'waste'))
    const boxes = Math.ceil(adjusted / Math.max(value(values, 'boxCoverage'), 1))
    const total = boxes * value(values, 'pricePerBox')

    return result(
      'Boxes to buy',
      `${whole(boxes)} boxes`,
      `Estimated material spend is ${currency(total)} before accessories and labor.`,
      [
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Box coverage', value: `${formatNumber(value(values, 'boxCoverage'))} sq ft` },
        { label: 'Material budget', value: currency(total) }
      ],
      [
        `Area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted area = ${formatNumber(area)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Boxes = ${formatNumber(adjusted)} / ${value(values, 'boxCoverage')} = ${whole(boxes)} boxes.`,
        `Material budget = ${whole(boxes)} x ${currency(value(values, 'pricePerBox'))} = ${currency(total)}.`
      ]
    )
  },
  tile(values) {
    const tileArea = Math.max((value(values, 'tileLength') * value(values, 'tileWidth')) / 144, 0.01)
    const adjusted = value(values, 'area') * percentFactor(value(values, 'waste'))
    const tiles = Math.ceil(adjusted / tileArea)

    return result(
      'Tiles to buy',
      `${whole(tiles)} tiles`,
      `Each tile covers about ${formatNumber(tileArea, 2)} sq ft.`,
      [
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Tile area', value: `${formatNumber(tileArea, 2)} sq ft` },
        { label: 'Raw tile count', value: formatNumber(adjusted / tileArea, 1) }
      ],
      [
        `Tile area = ${value(values, 'tileLength')} in x ${value(values, 'tileWidth')} in / 144 = ${formatNumber(tileArea, 2)} sq ft.`,
        `Adjusted area = ${value(values, 'area')} sq ft x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Tiles = ${formatNumber(adjusted)} / ${formatNumber(tileArea, 2)} = ${whole(tiles)} tiles.`
      ]
    )
  },
  tileCost(values) {
    const tileArea = Math.max((value(values, 'tileLength') * value(values, 'tileWidth')) / 144, 0.01)
    const adjusted = value(values, 'area') * percentFactor(value(values, 'waste'))
    const tiles = Math.ceil(adjusted / tileArea)
    const tileMaterial = tiles * value(values, 'pricePerTile')
    const setting = value(values, 'area') * value(values, 'settingCost')
    const total = tileMaterial + setting

    return result(
      'Estimated tile material cost',
      currency(total),
      `${whole(tiles)} tiles plus setting material allowance.`,
      [
        { label: 'Tiles to buy', value: `${whole(tiles)} tiles` },
        { label: 'Tile cost', value: currency(tileMaterial) },
        { label: 'Setting supplies', value: currency(setting) },
        { label: 'Estimated total', value: currency(total) }
      ],
      [
        `Tile area = ${formatNumber(tileArea, 2)} sq ft.`,
        `Adjusted tile area = ${value(values, 'area')} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Tile cost = ${whole(tiles)} x ${currency(value(values, 'pricePerTile'))} = ${currency(tileMaterial)}.`,
        `Total = tile cost ${currency(tileMaterial)} + setting supplies ${currency(setting)} = ${currency(total)}.`
      ]
    )
  },
  grout(values) {
    const tileLength = Math.max(value(values, 'tileLength'), 0.01)
    const tileWidth = Math.max(value(values, 'tileWidth'), 0.01)
    const factor = ((tileLength + tileWidth) / (tileLength * tileWidth)) * value(values, 'jointWidth') * value(values, 'jointDepth')
    const pounds = value(values, 'area') * factor * 20 * percentFactor(value(values, 'waste'))
    const bags = Math.ceil(pounds / 10)

    return result(
      'Estimated grout',
      `${formatNumber(pounds)} lb`,
      `Plan around ${whole(bags)} ten-pound bags, then confirm against the grout manufacturer chart.`,
      [
        { label: 'Estimated grout weight', value: `${formatNumber(pounds)} lb` },
        { label: '10 lb bags', value: `${whole(bags)} bags` },
        { label: 'Joint width', value: `${value(values, 'jointWidth')} in` },
        { label: 'Joint depth', value: `${value(values, 'jointDepth')} in` }
      ],
      [
        `Tile joint factor = (length + width) / (length x width) x joint width x joint depth.`,
        `Factor = (${tileLength} + ${tileWidth}) / (${tileLength} x ${tileWidth}) x ${value(values, 'jointWidth')} x ${value(values, 'jointDepth')} = ${formatNumber(factor, 4)}.`,
        `Estimated pounds = area ${value(values, 'area')} x factor x planning density x waste = ${formatNumber(pounds)} lb.`,
        `Bags = ${formatNumber(pounds)} / 10 lb, rounded up to ${whole(bags)} bags.`
      ]
    )
  },
  concrete(values) {
    const cubicFeet = value(values, 'length') * value(values, 'width') * (value(values, 'depth') / 12)
    const adjustedCubicFeet = cubicFeet * percentFactor(value(values, 'waste'))
    const cubicYards = adjustedCubicFeet / 27
    const bags80 = Math.ceil(adjustedCubicFeet / 0.6)

    return result(
      'Concrete needed',
      `${formatNumber(cubicYards, 2)} cu yd`,
      `For small pours, this is about ${whole(bags80)} standard 80 lb bags.`,
      [
        { label: 'Cubic feet', value: `${formatNumber(cubicFeet)} cu ft` },
        { label: 'Adjusted volume', value: `${formatNumber(adjustedCubicFeet)} cu ft` },
        { label: 'Cubic yards', value: `${formatNumber(cubicYards, 2)} cu yd` },
        { label: '80 lb bags', value: `${whole(bags80)} bags` }
      ],
      [
        `Depth in feet = ${value(values, 'depth')} in / 12 = ${formatNumber(value(values, 'depth') / 12, 2)} ft.`,
        `Cubic feet = ${value(values, 'length')} x ${value(values, 'width')} x ${formatNumber(value(values, 'depth') / 12, 2)} = ${formatNumber(cubicFeet)} cu ft.`,
        `Adjusted volume = ${formatNumber(cubicFeet)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjustedCubicFeet)} cu ft.`,
        `Cubic yards = ${formatNumber(adjustedCubicFeet)} / 27 = ${formatNumber(cubicYards, 2)} cu yd.`
      ]
    )
  },
  bulkMaterial(values) {
    const cubicFeet = value(values, 'length') * value(values, 'width') * (value(values, 'depth') / 12)
    const cubicYards = cubicFeet * percentFactor(value(values, 'waste')) / 27
    const tons = cubicYards * value(values, 'tonsPerCubicYard')

    return result(
      'Bulk material needed',
      `${formatNumber(cubicYards, 2)} cu yd`,
      `This is roughly ${formatNumber(tons, 2)} tons at the density entered.`,
      [
        { label: 'Cubic yards', value: `${formatNumber(cubicYards, 2)} cu yd` },
        { label: 'Estimated tons', value: `${formatNumber(tons, 2)} tons` },
        { label: 'Coverage area', value: `${formatNumber(value(values, 'length') * value(values, 'width'))} sq ft` }
      ],
      [
        `Area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(value(values, 'length') * value(values, 'width'))} sq ft.`,
        `Volume = area x depth ${value(values, 'depth')} in / 12 = ${formatNumber(cubicFeet)} cu ft.`,
        `Cubic yards = ${formatNumber(cubicFeet)} x waste factor / 27 = ${formatNumber(cubicYards, 2)} cu yd.`,
        `Tons = ${formatNumber(cubicYards, 2)} x ${value(values, 'tonsPerCubicYard')} = ${formatNumber(tons, 2)} tons.`
      ]
    )
  },
  mulch(values) {
    const cubicFeet = value(values, 'length') * value(values, 'width') * (value(values, 'depth') / 12) * percentFactor(value(values, 'waste'))
    const cubicYards = cubicFeet / 27
    const bags = Math.ceil(cubicFeet / Math.max(value(values, 'bagSize'), 0.1))

    return result(
      'Material volume',
      `${formatNumber(cubicYards, 2)} cu yd`,
      `That equals about ${whole(bags)} bags at ${value(values, 'bagSize')} cu ft each.`,
      [
        { label: 'Cubic feet', value: `${formatNumber(cubicFeet)} cu ft` },
        { label: 'Cubic yards', value: `${formatNumber(cubicYards, 2)} cu yd` },
        { label: 'Bag count', value: `${whole(bags)} bags` }
      ],
      [
        `Area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(value(values, 'length') * value(values, 'width'))} sq ft.`,
        `Cubic feet = area x ${value(values, 'depth')} in / 12 x waste factor = ${formatNumber(cubicFeet)} cu ft.`,
        `Cubic yards = ${formatNumber(cubicFeet)} / 27 = ${formatNumber(cubicYards, 2)} cu yd.`,
        `Bags = ${formatNumber(cubicFeet)} / ${value(values, 'bagSize')} = ${whole(bags)} bags.`
      ]
    )
  },
  drywall(values) {
    const netArea = positive(value(values, 'wallArea') + value(values, 'ceilingArea') - value(values, 'openings'))
    const adjusted = netArea * percentFactor(value(values, 'waste'))
    const sheetArea = Math.max(value(values, 'sheetLength') * value(values, 'sheetWidth'), 1)
    const sheets = Math.ceil(adjusted / sheetArea)
    const compoundGallons = Math.ceil(sheets / 3)
    const tapeFeet = Math.ceil(sheets * 12)

    return result(
      'Drywall sheets',
      `${whole(sheets)} sheets`,
      `Plan for ${formatNumber(adjusted)} sq ft of drywall surface after openings and waste.`,
      [
        { label: 'Net drywall area', value: `${formatNumber(netArea)} sq ft` },
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Joint compound', value: `${whole(compoundGallons)} gallons` },
        { label: 'Tape allowance', value: `${whole(tapeFeet)} linear ft` }
      ],
      [
        `Net area = wall area ${value(values, 'wallArea')} + ceiling area ${value(values, 'ceilingArea')} - openings ${value(values, 'openings')} = ${formatNumber(netArea)} sq ft.`,
        `Adjusted area = ${formatNumber(netArea)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Sheet area = ${value(values, 'sheetLength')} ft x ${value(values, 'sheetWidth')} ft = ${formatNumber(sheetArea)} sq ft.`,
        `Sheets = ${formatNumber(adjusted)} / ${formatNumber(sheetArea)} = ${whole(sheets)} sheets.`
      ]
    )
  },
  insulation(values) {
    const adjusted = value(values, 'area') * percentFactor(value(values, 'waste'))
    const bags = Math.ceil(adjusted / Math.max(value(values, 'coveragePerBag'), 1))
    const total = bags * value(values, 'pricePerBag')

    return result(
      'Packages to buy',
      `${whole(bags)} packages`,
      `Estimated insulation material budget is ${currency(total)}.`,
      [
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Coverage per package', value: `${formatNumber(value(values, 'coveragePerBag'))} sq ft` },
        { label: 'Material budget', value: currency(total) }
      ],
      [
        `Adjusted area = ${value(values, 'area')} sq ft x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Packages = ${formatNumber(adjusted)} / ${value(values, 'coveragePerBag')} = ${whole(bags)} packages.`,
        `Material budget = ${whole(bags)} x ${currency(value(values, 'pricePerBag'))} = ${currency(total)}.`
      ]
    )
  },
  fence(values) {
    const posts = Math.ceil(value(values, 'length') / Math.max(value(values, 'postSpacing'), 1)) + 1 + value(values, 'gates')
    const panels = Math.ceil(value(values, 'length') / Math.max(value(values, 'panelWidth'), 1))
    const concreteBags = Math.ceil(posts * value(values, 'concretePerPost'))

    return result(
      'Fence posts',
      `${whole(posts)} posts`,
      `Plan around ${whole(panels)} panels and ${whole(concreteBags)} concrete bags.`,
      [
        { label: 'Fence panels', value: `${whole(panels)} panels` },
        { label: 'Posts', value: `${whole(posts)} posts` },
        { label: 'Concrete bags', value: `${whole(concreteBags)} bags` },
        { label: 'Gates included', value: `${whole(value(values, 'gates'))}` }
      ],
      [
        `Base posts = length ${value(values, 'length')} / spacing ${value(values, 'postSpacing')}, rounded up, plus end post.`,
        `Gate allowance adds ${value(values, 'gates')} extra post positions.`,
        `Panels = ${value(values, 'length')} / ${value(values, 'panelWidth')} = ${whole(panels)} panels.`,
        `Concrete = ${whole(posts)} posts x ${value(values, 'concretePerPost')} bags = ${whole(concreteBags)} bags.`
      ]
    )
  },
  deck(values) {
    const area = areaFrom(values)
    const rowModule = Math.max((value(values, 'boardWidth') + value(values, 'gap')) / 12, 0.01)
    const rows = Math.ceil(value(values, 'width') / rowModule)
    const boardsPerRow = Math.ceil(value(values, 'length') / Math.max(value(values, 'boardLength'), 1))
    const boards = rows * boardsPerRow
    const total = boards * value(values, 'pricePerBoard')

    return result(
      'Deck boards',
      `${whole(boards)} boards`,
      `This covers about ${formatNumber(area)} sq ft of deck surface before stairs, fascia, and railing.`,
      [
        { label: 'Deck area', value: `${formatNumber(area)} sq ft` },
        { label: 'Board rows', value: `${whole(rows)} rows` },
        { label: 'Boards per row', value: `${whole(boardsPerRow)}` },
        { label: 'Decking budget', value: currency(total) }
      ],
      [
        `Deck area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Board module = (${value(values, 'boardWidth')} in + ${value(values, 'gap')} in) / 12 = ${formatNumber(rowModule, 3)} ft.`,
        `Rows = deck width ${value(values, 'width')} / ${formatNumber(rowModule, 3)} = ${whole(rows)} rows.`,
        `Boards = ${whole(rows)} rows x ${whole(boardsPerRow)} boards per row = ${whole(boards)} boards.`
      ]
    )
  },
  patio(values) {
    const area = areaFrom(values)
    const adjusted = area * percentFactor(value(values, 'waste'))
    const paverArea = Math.max((value(values, 'paverLength') * value(values, 'paverWidth')) / 144, 0.01)
    const pavers = Math.ceil(adjusted / paverArea)
    const baseCubicYards = area * (value(values, 'baseDepth') / 12) / 27

    return result(
      'Pavers to buy',
      `${whole(pavers)} pavers`,
      `Plan for about ${formatNumber(baseCubicYards, 2)} cu yd of compacted base before supplier rounding.`,
      [
        { label: 'Patio area', value: `${formatNumber(area)} sq ft` },
        { label: 'Adjusted paver area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Paver area', value: `${formatNumber(paverArea, 2)} sq ft` },
        { label: 'Base material', value: `${formatNumber(baseCubicYards, 2)} cu yd` }
      ],
      [
        `Patio area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted paver area = ${formatNumber(area)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Paver area = ${value(values, 'paverLength')} in x ${value(values, 'paverWidth')} in / 144 = ${formatNumber(paverArea, 2)} sq ft.`,
        `Pavers = ${formatNumber(adjusted)} / ${formatNumber(paverArea, 2)} = ${whole(pavers)} pavers.`
      ]
    )
  },
  garden(values) {
    const area = areaFrom(values)
    const soilCuFt = area * (value(values, 'soilDepth') / 12)
    const compostCuFt = soilCuFt * positive(value(values, 'compostShare')) / 100
    const mulchCuFt = area * (value(values, 'mulchDepth') / 12)

    return result(
      'Garden soil mix',
      `${formatNumber(soilCuFt)} cu ft`,
      `Blend about ${formatNumber(compostCuFt)} cu ft of compost and plan ${formatNumber(mulchCuFt)} cu ft of mulch.`,
      [
        { label: 'Bed area', value: `${formatNumber(area)} sq ft` },
        { label: 'Soil volume', value: `${formatNumber(soilCuFt)} cu ft` },
        { label: 'Compost portion', value: `${formatNumber(compostCuFt)} cu ft` },
        { label: 'Mulch top layer', value: `${formatNumber(mulchCuFt)} cu ft` }
      ],
      [
        `Bed area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Soil volume = ${formatNumber(area)} x ${value(values, 'soilDepth')} in / 12 = ${formatNumber(soilCuFt)} cu ft.`,
        `Compost = ${formatNumber(soilCuFt)} x ${value(values, 'compostShare')}% = ${formatNumber(compostCuFt)} cu ft.`,
        `Mulch = ${formatNumber(area)} x ${value(values, 'mulchDepth')} in / 12 = ${formatNumber(mulchCuFt)} cu ft.`
      ]
    )
  },
  sealer(values) {
    const area = areaFrom(values)
    const adjusted = area * value(values, 'coats') * percentFactor(value(values, 'waste'))
    const buckets = Math.ceil(adjusted / Math.max(value(values, 'coverage'), 1))

    return result(
      'Sealer buckets',
      `${whole(buckets)} buckets`,
      `The coated coverage target is ${formatNumber(adjusted)} sq ft.`,
      [
        { label: 'Driveway area', value: `${formatNumber(area)} sq ft` },
        { label: 'Adjusted coated area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Bucket coverage', value: `${formatNumber(value(values, 'coverage'))} sq ft` }
      ],
      [
        `Driveway area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(area)} sq ft.`,
        `Adjusted coated area = ${formatNumber(area)} x ${value(values, 'coats')} coats x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Buckets = ${formatNumber(adjusted)} / ${value(values, 'coverage')} = ${whole(buckets)} buckets.`
      ]
    )
  },
  roofArea(values) {
    const footprint = areaFrom(values)
    const pitched = footprint * value(values, 'pitchMultiplier')
    const adjusted = pitched * percentFactor(value(values, 'waste'))
    const squares = adjusted / 100

    return result(
      'Roof area',
      `${formatNumber(adjusted)} sq ft`,
      `That is about ${formatNumber(squares, 1)} roofing squares after waste.`,
      [
        { label: 'Footprint area', value: `${formatNumber(footprint)} sq ft` },
        { label: 'Pitch-adjusted area', value: `${formatNumber(pitched)} sq ft` },
        { label: 'Waste-adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Roofing squares', value: `${formatNumber(squares, 1)} squares` }
      ],
      [
        `Footprint = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(footprint)} sq ft.`,
        `Pitch-adjusted area = ${formatNumber(footprint)} x ${value(values, 'pitchMultiplier')} = ${formatNumber(pitched)} sq ft.`,
        `Waste-adjusted area = ${formatNumber(pitched)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Roofing squares = ${formatNumber(adjusted)} / 100 = ${formatNumber(squares, 1)} squares.`
      ]
    )
  },
  roofingMaterial(values) {
    const adjusted = value(values, 'roofArea') * percentFactor(value(values, 'waste'))
    const squares = adjusted / 100
    const bundles = Math.ceil(squares * value(values, 'bundlesPerSquare'))
    const underlayment = Math.ceil(adjusted / Math.max(value(values, 'underlaymentRollCoverage'), 1))

    return result(
      'Roofing bundles',
      `${whole(bundles)} bundles`,
      `Plan around ${formatNumber(squares, 1)} roofing squares and ${whole(underlayment)} underlayment rolls.`,
      [
        { label: 'Adjusted roof area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Roofing squares', value: `${formatNumber(squares, 1)} squares` },
        { label: 'Shingle bundles', value: `${whole(bundles)} bundles` },
        { label: 'Underlayment rolls', value: `${whole(underlayment)} rolls` }
      ],
      [
        `Adjusted roof area = ${value(values, 'roofArea')} sq ft x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Roofing squares = ${formatNumber(adjusted)} / 100 = ${formatNumber(squares, 1)} squares.`,
        `Bundles = ${formatNumber(squares, 1)} x ${value(values, 'bundlesPerSquare')} = ${whole(bundles)} bundles.`,
        `Underlayment rolls = ${formatNumber(adjusted)} / ${value(values, 'underlaymentRollCoverage')} = ${whole(underlayment)} rolls.`
      ]
    )
  },
  gutter(values) {
    const downspouts = Math.max(2, Math.ceil(value(values, 'gutterLength') / Math.max(value(values, 'downspoutSpacing'), 1)))
    const gutterCost = value(values, 'gutterLength') * value(values, 'pricePerFoot')
    const downspoutCost = downspouts * value(values, 'downspoutCost')
    const total = gutterCost + downspoutCost

    return result(
      'Estimated gutter budget',
      currency(total),
      `Plan for ${formatNumber(value(values, 'gutterLength'))} ft of gutter and ${whole(downspouts)} downspouts.`,
      [
        { label: 'Gutter length', value: `${formatNumber(value(values, 'gutterLength'))} ft` },
        { label: 'Downspouts', value: `${whole(downspouts)}` },
        { label: 'Gutter material', value: currency(gutterCost) },
        { label: 'Downspout allowance', value: currency(downspoutCost) }
      ],
      [
        `Downspouts = gutter length ${value(values, 'gutterLength')} / spacing ${value(values, 'downspoutSpacing')}, rounded up with a two-downspout minimum.`,
        `Gutter material = ${value(values, 'gutterLength')} ft x ${currency(value(values, 'pricePerFoot'))} = ${currency(gutterCost)}.`,
        `Downspout allowance = ${whole(downspouts)} x ${currency(value(values, 'downspoutCost'))} = ${currency(downspoutCost)}.`,
        `Total = ${currency(gutterCost)} + ${currency(downspoutCost)} = ${currency(total)}.`
      ]
    )
  },
  siding(values) {
    const net = positive(value(values, 'wallArea') - value(values, 'openings'))
    const adjusted = net * percentFactor(value(values, 'waste'))
    const squares = adjusted / 100
    const buySquares = Math.ceil(squares)
    const total = buySquares * value(values, 'pricePerSquare')

    return result(
      'Siding squares',
      `${whole(buySquares)} squares`,
      `Estimated material allowance is ${currency(total)} before trim, labor, and removal.`,
      [
        { label: 'Net wall area', value: `${formatNumber(net)} sq ft` },
        { label: 'Adjusted siding area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Raw squares', value: `${formatNumber(squares, 1)} squares` },
        { label: 'Material allowance', value: currency(total) }
      ],
      [
        `Net siding area = ${value(values, 'wallArea')} - openings ${value(values, 'openings')} = ${formatNumber(net)} sq ft.`,
        `Adjusted area = ${formatNumber(net)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Squares = ${formatNumber(adjusted)} / 100 = ${formatNumber(squares, 1)}, rounded up to ${whole(buySquares)}.`,
        `Material allowance = ${whole(buySquares)} x ${currency(value(values, 'pricePerSquare'))} = ${currency(total)}.`
      ]
    )
  },
  squareFootage(values) {
    const single = value(values, 'length') * value(values, 'width')
    const total = single * value(values, 'quantity')

    return result(
      'Total square footage',
      `${formatNumber(total)} sq ft`,
      `Each matching space is ${formatNumber(single)} sq ft.`,
      [
        { label: 'Single space area', value: `${formatNumber(single)} sq ft` },
        { label: 'Number of spaces', value: `${formatNumber(value(values, 'quantity'), 0)}` },
        { label: 'Total area', value: `${formatNumber(total)} sq ft` }
      ],
      [
        `Single space = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(single)} sq ft.`,
        `Total = ${formatNumber(single)} sq ft x ${value(values, 'quantity')} spaces = ${formatNumber(total)} sq ft.`
      ]
    )
  },
  roomSize(values) {
    const floor = areaFrom(values)
    const perimeter = 2 * (value(values, 'length') + value(values, 'width'))
    const grossWall = perimeter * value(values, 'height')
    const netWall = positive(grossWall - value(values, 'openings'))
    const volume = floor * value(values, 'height')

    return result(
      'Room floor area',
      `${formatNumber(floor)} sq ft`,
      `The room has ${formatNumber(perimeter)} ft of perimeter and ${formatNumber(netWall)} sq ft of net wall area.`,
      [
        { label: 'Floor area', value: `${formatNumber(floor)} sq ft` },
        { label: 'Perimeter', value: `${formatNumber(perimeter)} ft` },
        { label: 'Net wall area', value: `${formatNumber(netWall)} sq ft` },
        { label: 'Room volume', value: `${formatNumber(volume)} cu ft` }
      ],
      [
        `Floor area = ${value(values, 'length')} ft x ${value(values, 'width')} ft = ${formatNumber(floor)} sq ft.`,
        `Perimeter = 2 x (${value(values, 'length')} + ${value(values, 'width')}) = ${formatNumber(perimeter)} ft.`,
        `Gross wall area = ${formatNumber(perimeter)} x height ${value(values, 'height')} = ${formatNumber(grossWall)} sq ft.`,
        `Net wall area = ${formatNumber(grossWall)} - openings ${value(values, 'openings')} = ${formatNumber(netWall)} sq ft.`
      ]
    )
  },
  renovationCost(values) {
    const base = value(values, 'area') * value(values, 'costPerSqFt')
    const labor = base * positive(value(values, 'laborShare')) / 100
    const materials = positive(base - labor)
    const contingency = base * positive(value(values, 'contingency')) / 100
    const total = base + contingency

    return result(
      'Planning budget',
      currency(total),
      `Base scope is ${currency(base)} plus ${currency(contingency)} contingency.`,
      [
        { label: 'Base project cost', value: currency(base) },
        { label: 'Material allowance', value: currency(materials) },
        { label: 'Labor allowance', value: currency(labor) },
        { label: 'Contingency', value: currency(contingency) }
      ],
      [
        `Base cost = ${value(values, 'area')} sq ft x ${currency(value(values, 'costPerSqFt'))} = ${currency(base)}.`,
        `Labor allowance = ${currency(base)} x ${value(values, 'laborShare')}% = ${currency(labor)}.`,
        `Material allowance = ${currency(base)} - ${currency(labor)} = ${currency(materials)}.`,
        `Total budget = base ${currency(base)} + contingency ${currency(contingency)} = ${currency(total)}.`
      ]
    )
  },
  budget(values) {
    const totalBudget = value(values, 'budget')
    const materials = totalBudget * positive(value(values, 'materialsShare')) / 100
    const labor = totalBudget * positive(value(values, 'laborShare')) / 100
    const permits = totalBudget * positive(value(values, 'permitsShare')) / 100
    const contingency = totalBudget * positive(value(values, 'contingencyShare')) / 100
    const allocated = materials + labor + permits + contingency
    const remaining = totalBudget - allocated

    return result(
      'Allocated budget',
      currency(allocated),
      remaining >= 0 ? `${currency(remaining)} remains unallocated.` : `The shares exceed the budget by ${currency(Math.abs(remaining))}.`,
      [
        { label: 'Materials', value: currency(materials) },
        { label: 'Labor', value: currency(labor) },
        { label: 'Permits and design', value: currency(permits) },
        { label: 'Contingency', value: currency(contingency) },
        { label: 'Remaining', value: currency(remaining) }
      ],
      [
        `Materials = ${currency(totalBudget)} x ${value(values, 'materialsShare')}% = ${currency(materials)}.`,
        `Labor = ${currency(totalBudget)} x ${value(values, 'laborShare')}% = ${currency(labor)}.`,
        `Permits and design = ${currency(totalBudget)} x ${value(values, 'permitsShare')}% = ${currency(permits)}.`,
        `Contingency = ${currency(totalBudget)} x ${value(values, 'contingencyShare')}% = ${currency(contingency)}.`
      ]
    )
  },
  countertop(values) {
    const counterArea = value(values, 'length') * (value(values, 'depth') / 12)
    const baseArea = counterArea + value(values, 'islandArea')
    const adjusted = baseArea * percentFactor(value(values, 'waste'))
    const total = adjusted * value(values, 'pricePerSqFt')

    return result(
      'Countertop area',
      `${formatNumber(adjusted)} sq ft`,
      `Estimated material allowance is ${currency(total)} before fabrication details and removal.`,
      [
        { label: 'Run area', value: `${formatNumber(counterArea)} sq ft` },
        { label: 'Base area with island', value: `${formatNumber(baseArea)} sq ft` },
        { label: 'Adjusted area', value: `${formatNumber(adjusted)} sq ft` },
        { label: 'Material allowance', value: currency(total) }
      ],
      [
        `Run area = ${value(values, 'length')} linear ft x ${value(values, 'depth')} in / 12 = ${formatNumber(counterArea)} sq ft.`,
        `Base area = ${formatNumber(counterArea)} + island area ${value(values, 'islandArea')} = ${formatNumber(baseArea)} sq ft.`,
        `Adjusted area = ${formatNumber(baseArea)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} sq ft.`,
        `Material allowance = ${formatNumber(adjusted)} x ${currency(value(values, 'pricePerSqFt'))} = ${currency(total)}.`
      ]
    )
  },
  trim(values) {
    const net = positive(value(values, 'perimeter') - value(values, 'openings'))
    const adjusted = net * percentFactor(value(values, 'waste'))
    const pieces = Math.ceil(adjusted / Math.max(value(values, 'pieceLength'), 1))
    const total = pieces * value(values, 'pricePerPiece')

    return result(
      'Trim pieces',
      `${whole(pieces)} pieces`,
      `Plan for ${formatNumber(adjusted)} linear feet of trim after door openings and waste.`,
      [
        { label: 'Net trim length', value: `${formatNumber(net)} ft` },
        { label: 'Adjusted length', value: `${formatNumber(adjusted)} ft` },
        { label: 'Pieces to buy', value: `${whole(pieces)}` },
        { label: 'Material budget', value: currency(total) }
      ],
      [
        `Net length = perimeter ${value(values, 'perimeter')} - openings ${value(values, 'openings')} = ${formatNumber(net)} ft.`,
        `Adjusted length = ${formatNumber(net)} x ${percentFactor(value(values, 'waste')).toFixed(2)} = ${formatNumber(adjusted)} ft.`,
        `Pieces = ${formatNumber(adjusted)} / ${value(values, 'pieceLength')} = ${whole(pieces)} pieces.`,
        `Material budget = ${whole(pieces)} x ${currency(value(values, 'pricePerPiece'))} = ${currency(total)}.`
      ]
    )
  }
}

export const runCalculator = (formulaKey, values) => {
  const engine = engines[formulaKey] || engines.squareFootage
  return engine(values)
}
